import { Moment } from "moment";
import { BookingPill } from "PFApp/components/pills/booking_pill";
import { Tooltip } from "PFComponents/tooltip";
import { utc } from "PFCore/helpers/date";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { AccessLevel } from "PFTypes";
import { MouseEvent, ReactNode, RefObject, useMemo, useState } from "react";

import css from "./bar.module.scss";

const daysToMs = (days: number) => days * 24 * 3600 * 1000;

type BarProps = {
  bookingCategoryId?: number;
  width: number;
  left: number;
  top: number;
  tooltipContent: ReactNode;
  dayWidth: number;
  startTime?: Moment;
  forceBg?: string;
  viewportRef?: RefObject<HTMLElement | null>;
};

export const Bar = ({
  bookingCategoryId,
  width,
  left,
  top,
  tooltipContent,
  dayWidth,
  startTime,
  forceBg,
  viewportRef
}: BarProps) => {
  const { formatDate } = useDateFormatter();

  const [tooltipFooter, setTooltipFooter] = useState<ReactNode>("");

  const { getBookingCategory } = useBookingCategories();
  const category = getBookingCategory(bookingCategoryId);
  const isCategoryHidden = !category || category.bookings_permissions === AccessLevel.Hidden;

  const tooltipContentComplete = useMemo(
    () => (
      <>
        {tooltipContent}
        <div style={{ marginTop: 5, textAlign: "center" }}>{tooltipFooter}</div>
      </>
    ),
    [tooltipContent, tooltipFooter]
  );

  const handleMouseMove = (event: MouseEvent<HTMLButtonElement>) => {
    const target = event.target as HTMLElement;
    const targetDimensions = target.getBoundingClientRect();
    const dayCount = targetDimensions.width / dayWidth;
    const dayIndex = Math.floor(
      ((event.clientX - targetDimensions.left) / targetDimensions.width) * dayCount
    );

    const targetDay = formatDate(utc((startTime?.valueOf() ?? 0) + daysToMs(dayIndex)));

    setTooltipFooter(targetDay);
  };

  const handleMouseLeave = () => setTooltipFooter("");

  return (
    <Tooltip
      placement="bottom"
      interactive
      content={tooltipContentComplete}
      triggerContainerProps={{
        className: css.root,
        style: {
          width,
          minWidth: dayWidth,
          transform: `translate(${left}px, ${top}px)`
        }
      }}
      middlewareProps={{
        shift: {
          boundary: viewportRef?.current ?? undefined
        }
      }}
    >
      <button
        className={css.bar}
        onMouseMove={!isCategoryHidden ? handleMouseMove : undefined}
        onMouseLeave={!isCategoryHidden ? handleMouseLeave : undefined}
      >
        <BookingPill
          className={css.pill}
          category={forceBg ? ({ color: forceBg } as any) : !isCategoryHidden ? category : undefined}
          withCategoryIcon={false}
        />
      </button>
    </Tooltip>
  );
};
